import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { Button, Input, Label } from '@pantry/ui';
import { useAuth } from '../../contexts/useAuth';
import { EyeIcon, EyeSlashIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

const searchSchema = z.object({
  redirect: z.string().optional(),
});

export const Route = createFileRoute('/_auth/login')({
  validateSearch: searchSchema,
  component: LoginPage,
});

function LoginPage() {
  const { t } = useLingui();
  const { redirect } = Route.useSearch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loginMode, setLoginMode] = useState<'password' | 'magic-link'>('password');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<'apple' | 'google' | null>(null);
  const { signIn, signInWithMagicLink, signInWithOAuth } = useAuth();
  const navigate = useNavigate();

  const handleOAuthLogin = async (provider: 'apple' | 'google') => {
    setError(null);
    setOauthLoading(provider);
    try {
      await signInWithOAuth(provider, redirect);
      // Redirect happens automatically via Supabase
    } catch (err) {
      setError(err instanceof Error ? err.message : t`Failed to sign in with ${provider}`);
      setOauthLoading(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (loginMode === 'magic-link') {
        await signInWithMagicLink(email, redirect);
        setMagicLinkSent(true);
      } else {
        await signIn(email, password);
        // Redirect to the specified URL or default to dashboard
        if (redirect) {
          window.location.href = redirect;
        } else {
          navigate({ to: '/dashboard' });
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t`Failed to sign in`);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleLoginMode = () => {
    setLoginMode(loginMode === 'password' ? 'magic-link' : 'password');
    setError(null);
    setMagicLinkSent(false);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="font-display text-3xl font-semibold tracking-tight">
          <Trans>Welcome back</Trans>
        </h1>
        <p className="text-muted-foreground">
          <Trans>Sign in to your account to continue managing your inventory.</Trans>
        </p>
      </div>

      {/* Social login buttons */}
      <div className="space-y-3">
        {/* Apple first on web */}
        <Button
          type="button"
          variant="outline"
          className="w-full h-11 font-medium"
          onClick={() => handleOAuthLogin('apple')}
          disabled={oauthLoading !== null}
        >
          {oauthLoading === 'apple' ? (
            <svg className="mr-2 animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
          )}
          <Trans>Continue with Apple</Trans>
        </Button>
        <Button
          type="button"
          variant="outline"
          className="w-full h-11 font-medium"
          onClick={() => handleOAuthLogin('google')}
          disabled={oauthLoading !== null}
        >
          {oauthLoading === 'google' ? (
            <svg className="mr-2 animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
          )}
          <Trans>Continue with Google</Trans>
        </Button>
      </div>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">
            <Trans>Or continue with email</Trans>
          </span>
        </div>
      </div>

      {/* Email/Password form */}
      {magicLinkSent ? (
        <div className="space-y-6 text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
            <EnvelopeIcon className="h-8 w-8 text-primary" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold">
              <Trans>Check your email</Trans>
            </h2>
            <p className="text-muted-foreground">
              <Trans>
                We sent a magic link to <span className="font-medium text-foreground">{email}</span>
              </Trans>
            </p>
            <p className="text-sm text-muted-foreground">
              <Trans>
                Click the link in your email to sign in. The link will expire in 1 hour.
              </Trans>
            </p>
          </div>
          <div className="pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setMagicLinkSent(false);
                setEmail('');
              }}
              className="text-sm"
            >
              <Trans>Use a different email</Trans>
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive animate-in">
              <div className="flex items-center gap-2">
                <svg
                  className="h-4 w-4 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {error}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">
              <Trans>Email address</Trans>
            </Label>
            <Input
              id="email"
              type="email"
              placeholder={t`you@example.com`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="h-11"
            />
          </div>

          {loginMode === 'password' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium">
                  <Trans>Password</Trans>
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  <Trans>Forgot password?</Trans>
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t`Enter your password`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          )}

          <Button type="submit" className="w-full h-11 font-medium" disabled={isLoading}>
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {loginMode === 'magic-link' ? (
                  <Trans>Sending link...</Trans>
                ) : (
                  <Trans>Signing in...</Trans>
                )}
              </span>
            ) : loginMode === 'magic-link' ? (
              <span className="flex items-center gap-2">
                <EnvelopeIcon className="h-5 w-5" />
                <Trans>Send magic link</Trans>
              </span>
            ) : (
              <Trans>Sign in</Trans>
            )}
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={toggleLoginMode}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {loginMode === 'password' ? (
                <Trans>Sign in with magic link instead</Trans>
              ) : (
                <Trans>Sign in with password instead</Trans>
              )}
            </button>
          </div>
        </form>
      )}

      {/* Sign up link */}
      <p className="text-center text-sm text-muted-foreground">
        <Trans>Don&apos;t have an account?</Trans>{' '}
        <Link
          to="/signup"
          search={redirect ? { redirect } : undefined}
          className="font-medium text-primary hover:text-primary/80 transition-colors"
        >
          <Trans>Create an account</Trans>
        </Link>
      </p>
    </div>
  );
}
